import '../scss/style.scss';
import '../scss/tailwind.css';
import '@fancyapps/ui/dist/fancybox/fancybox.css';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import './password-protection';

import Swiper from 'swiper';
import {
  Navigation,
  Pagination,
  Controller,
  EffectFade,
  Autoplay,
  Scrollbar,
  Mousewheel,
  Thumbs,
} from 'swiper/modules';
import Lenis from 'lenis';
import { Fancybox } from '@fancyapps/ui';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

gsap.registerPlugin(ScrollTrigger, SplitText);

let lenisInstance;
let scrollTriggerInstance;

function initLenis() {
  const sphere = document.getElementById('sphere');

  if (!sphere) {
    console.warn('Sphere canvas not found');
  } else {
    const container = sphere.parentElement;
    const width = container.clientWidth || window.innerWidth;
    const height = container.clientHeight || window.innerHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);

    let cameraZ = 1;
    if (width <= 680) {
      cameraZ = 3.5;
    } else if (width <= 960) {
      cameraZ = 2.2;
    }
    camera.position.z = cameraZ;
    camera.position.y = 0;
    camera.position.x = 0;
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({
      canvas: sphere,
      alpha: true,
      antialias: true,
    });

    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    const vertexShader = `
      varying vec4 vScreenPosition;
      varying vec2 vUv;

      void main() {
        vUv = uv;

        vec4 viewPos = modelViewMatrix * vec4(position, 1.0);
        
        vec4 projectedPos = projectionMatrix * viewPos;
        vScreenPosition = projectedPos;

        gl_Position = projectedPos;
      }
    `;

    const fragmentShader = `
      uniform float uAspect;
      uniform float uTime;
      varying vec4 vScreenPosition;
      varying vec2 vUv;

      void main() {
        float time = uTime;
        
        vec2 center = vec2(0.5, 0.451);
        
        vec2 uvOffset = (vUv - center);
        uvOffset.x *= uAspect;
        float distanceFromCenter = length(uvOffset);
        
        float baseRadius = 0.6;
        
        float colorChangeSpeed = 0.2;
        float scaleCycle = sin(time * colorChangeSpeed) * 0.5 + 0.5;
        
        float scale = 1.0 + scaleCycle * 0.45;
        float maxRadius = baseRadius * scale;
        
        float normalizedDistance = distanceFromCenter / maxRadius;
        
        float waveSpeed = 0.1;
        
        float waveInterval = 10.0;
        
        float waveCount = floor(time / waveInterval);
        
        float wavePosition = mod(time, waveInterval) * waveSpeed;
        
        float waveDistance = abs(distanceFromCenter - wavePosition);
        
        float waveWidth = 0.15;
        float waveRing = exp(-waveDistance / waveWidth);
        
        float waveVisible = step(wavePosition, maxRadius * 1.5);
        
        float waveAttenuation = 1.0 - smoothstep(0.0, maxRadius * 1.3, normalizedDistance);
        float waveEffect = waveRing * waveAttenuation * waveVisible * 0.25;
        
        float colorCycle = scaleCycle;
        
        vec3 blueGradient1 = vec3(0.486, 0.859, 0.969);
        vec3 blueGradient2 = vec3(0.369, 0.737, 0.847);
        vec3 blueGradient3 = vec3(0.290, 0.655, 0.761);
        
        vec3 orangeGradient1 = vec3(0.98, 0.75, 0.55);
        vec3 orangeGradient2 = vec3(0.95, 0.65, 0.45);
        vec3 orangeGradient3 = vec3(0.93, 0.55, 0.35);
        
        vec3 color1 = mix(blueGradient1, orangeGradient1, colorCycle);
        float stop1 = 0.2062;
        
        vec3 color2 = mix(blueGradient2, orangeGradient2, colorCycle);
        float stop2 = 0.3173;
        
        vec3 color3 = mix(blueGradient3, orangeGradient3, colorCycle);
        float stop3 = 0.3942;
        
        vec3 color4 = vec3(1.0, 1.0, 1.0);
        float stop4 = 0.8365;
        
        vec3 finalColor;
        float alpha;
        
        if (normalizedDistance <= stop1) {
          finalColor = color1;
          alpha = 0.70;
        } else if (normalizedDistance <= stop2) {
          float t = smoothstep(stop1, stop2, normalizedDistance);
          finalColor = mix(color1, color2, t);
          alpha = 0.70;
        } else if (normalizedDistance <= stop3) {
          float t = smoothstep(stop2, stop3, normalizedDistance);
          finalColor = mix(color2, color3, t);
          alpha = 0.70;
        } else if (normalizedDistance <= stop4) {
          float t = smoothstep(stop3, stop4, normalizedDistance);
          finalColor = mix(color3, color4, t);
          alpha = mix(0.70, 0.0, t);
        } else {
          finalColor = vec3(1.0, 1.0, 1.0);
          alpha = 0.0;
        }
        
        vec3 waveColor = vec3(1.0, 1.0, 1.0);
        float waveIntensity = waveEffect;
        
        if (waveIntensity > 0.0005) {
          finalColor = mix(finalColor, waveColor, waveIntensity * 0.4);
          
          finalColor += waveIntensity * 0.1;
        }

        gl_FragColor = vec4(finalColor, alpha);
      }
    `;

    const aspect = width / height;
    const planeWidth = 3;
    const planeHeight = planeWidth / aspect;

    const geometry = new THREE.PlaneGeometry(planeWidth, planeHeight, 64, 64);
    const material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new THREE.Color(0x4aa7c2) },
        uGlowIntensity: { value: 2 },
        uRoughness: { value: 1 },
        uMetalness: { value: 2 },
        uAspect: { value: width / height },
      },
      transparent: true,
      side: THREE.DoubleSide,
      blending: THREE.NormalBlending,
      depthWrite: false,
    });

    const planeMesh = new THREE.Mesh(geometry, material);
    planeMesh.rotation.x = 0;
    planeMesh.position.z = 0;
    scene.add(planeMesh);

    function handleResize() {
      if (!sphere || !container) return;

      const width = container.clientWidth || window.innerWidth;
      const height = container.clientHeight || window.innerHeight;

      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);

      material.uniforms.uAspect.value = width / height;

      let newCameraZ = 1;
      if (width <= 680) {
        newCameraZ = 3.5;
      } else if (width <= 960) {
        newCameraZ = 2.2;
      }
      camera.position.z = newCameraZ;

      const aspect = width / height;
      const planeWidth = 3;
      const planeHeight = planeWidth / aspect;

      const newGeometry = new THREE.PlaneGeometry(
        planeWidth,
        planeHeight,
        64,
        64
      );
      planeMesh.geometry.dispose();
      planeMesh.geometry = newGeometry;
    }

    window.addEventListener('resize', handleResize);

    function animate() {
      requestAnimationFrame(animate);

      material.uniforms.uTime.value += 0.016;

      renderer.render(scene, camera);
    }

    animate();
  }

  if (lenisInstance) {
    lenisInstance.destroy();
  }

  if (scrollTriggerInstance) {
    scrollTriggerInstance.kill();
  }

  lenisInstance = new Lenis({
    lerp: 0.1,
    smoothWheel: true,
  });

  lenisInstance.on('scroll', ScrollTrigger.update);

  function raf(time) {
    lenisInstance.raf(time);
    requestAnimationFrame(raf);
  }
  requestAnimationFrame(raf);

  codeLoadpage();
}

function codeLoadpage() {
  Fancybox.unbind('[data-fancybox]');
  Fancybox.bind('[data-fancybox]', {
    placeFocusBack: false,
    hideScrollbar: false,
    autoFocus: false,
    trapFocus: false,
    hash: false,
    on: {
      init: () => {
        if (lenisInstance) lenisInstance.stop();
      },
      destroy: () => {
        if (lenisInstance) lenisInstance.start();
      },
    },
  });

  // modal profile
  const socialLinks = document.querySelectorAll('.profile-link .social-link');
  socialLinks.forEach((socialLink) => {
    socialLink.addEventListener('click', (e) => {
      e.stopPropagation();
    });
  });

  const profileLink = document.querySelectorAll('.profile-link');
  profileLink.forEach((link) => {
    link.addEventListener('click', (e) => {
      if (e.target.closest('.social-link')) return;

      const profile = link.dataset.profile;
      const name = link.dataset.name;
      const position = link.dataset.position;
      const infoId = link.dataset.infoId;
      const resumenId = link.dataset.resumenId;
      const linkedinId = link.dataset.linkedinId;

      const modalTeam = link.closest('.team-section').querySelector('.modal-team');
      modalTeam.classList.add('active');

      const imageProfile = modalTeam.querySelector('.image-profile');
      imageProfile.src = profile;

      const nameProfile = modalTeam.querySelector('.name-profile');
      nameProfile.textContent = name;

      const positionProfile = modalTeam.querySelector('.position-profile');
      positionProfile.textContent = position;

      const infoProfile = modalTeam.querySelector('.info-profile');
      const template = document.getElementById(infoId);
      if (template) {
        const content = template.content.cloneNode(true);
        infoProfile.innerHTML = '';
        infoProfile.appendChild(content);
      }

      const resumenProfile = modalTeam.querySelector('.resumen-profile');
      const resumenTemplate = resumenId ? document.getElementById(resumenId) : null;
      if (resumenProfile) {
        resumenProfile.innerHTML = '';
        if (resumenTemplate) {
          const resumenContent = resumenTemplate.content.cloneNode(true);
          resumenProfile.appendChild(resumenContent);
        }
      }

      const linkedinWrapper = modalTeam.querySelector('.linkedin-button-wrapper');
      if (linkedinWrapper) {
        linkedinWrapper.innerHTML = '';
        if (linkedinId) {
          const linkedinTemplate = document.getElementById(linkedinId);
          if (linkedinTemplate) {
            const linkedinContent = linkedinTemplate.content.cloneNode(true);
            linkedinWrapper.appendChild(linkedinContent);
          }
        }
      }

      const contactWrapper = modalTeam.querySelector('.contact-wrapper');
      if (contactWrapper) {
        contactWrapper.innerHTML = '';
        const formId = link.dataset.formId;
        if (formId) {
          const formTemplate = document.getElementById(formId);
          if (formTemplate) {
            contactWrapper.appendChild(formTemplate.content.cloneNode(true));
            contactWrapper.style.display = '';
          } else {
            contactWrapper.style.display = 'none';
          }
        } else {
          contactWrapper.style.display = 'none';
        }
      }

      const modalTeamClose = modalTeam.querySelectorAll('.modal-profile-close');
      modalTeamClose.forEach((close) => {
        close.addEventListener('click', (e) => {
          e.preventDefault();
          modalTeam.classList.remove('active');
        });
      });
    });
  });

  const titleSwiper = new Swiper('.title-swiper', {
    modules: [EffectFade, Autoplay],
    effect: 'fade',
    loop: true,
    speed: 1000,
    fadeEffect: {
      crossFade: true,
    },
    autoplay: {
      delay: 4000,
      disableOnInteraction: false,
    },
    on: {
      init: function () {
        // Disable autoplay and loop if only 1 slide
        if (this.slides.length <= 1) {
          this.autoplay.stop();
          this.params.loop = false;
        }
      },
      slideChange: function () {
        // Only execute title-split animation if there is more than 1 slide
        if (this.slides.length <= 1) return;

        // title split
        const titleSplit = document.querySelectorAll('.title-split');
        titleSplit.forEach((title) => {
          gsap.killTweensOf(title);

          const split = SplitText.create(title, {
            type: 'words',
            mask: 'words',
          });

          gsap.set(split.words, {
            y: '100%',
            opacity: 0,
          });

          gsap.to(split.words, {
            y: '0%',
            opacity: 1,
            duration: 1,
            ease: 'power3.out',
            stagger: 0.2,
          });
        });
      },
    },
  });

  const sliderBigImage = new Swiper('.mega-image', {
    modules: [EffectFade, Autoplay],
    effect: 'fade',
    loop: true,
    speed: 1000,
    fadeEffect: {
      crossFade: true,
    },
    autoplay: {
      delay: 3000,
      disableOnInteraction: false,
    },
  });

  const sliderMiniColumns = document.querySelectorAll('.slider-mini-columns');

  sliderMiniColumns.forEach((sliderEl) => {
    // Find the closest .relative parent and then find the pagination within it
    const parent = sliderEl.closest('.relative');
    const paginationEl = parent
      ? parent.querySelector('.swiper-pagination')
      : null;

    new Swiper(sliderEl, {
      modules: [EffectFade, Autoplay, Pagination],
      effect: 'fade',
      loop: true,
      speed: 1000,
      fadeEffect: {
        crossFade: true,
      },
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
      },
      pagination: {
        el: paginationEl,
        clickable: true,
      },
    });
  });

  const citesSlider = new Swiper('.cites-slider', {
    modules: [Navigation, Pagination],
    slidesPerView: 1,
    spaceBetween: 30,
    loop: true,
    speed: 1000,
    navigation: {
      nextEl: '.swiper-button-next-cites',
      prevEl: '.swiper-button-prev-cites',
    },
    pagination: {
      el: '.swiper-pagination-cites',
      clickable: true,
    },
  });

  const swiperPublications = new Swiper('.swiper-publications', {
    modules: [Navigation],
    slidesPerView: 2.5,
    spaceBetween: 44,
    speed: 1000,
    navigation: {
      nextEl: '.next-publications',
      prevEl: '.prev-publications',
    },
    breakpoints: {
      1000: {
        slidesPerView: 2.8,
      },
      680: {
        slidesPerView: 1.5,
        spaceBetween: 20,
      },
      0: {
        slidesPerView: 1.1,
        spaceBetween: 20,
      },
    },
  });

  const sliderNormal = document.querySelectorAll('.slider-normal');

  sliderNormal.forEach((sliderEl) => {
    const nextEl = sliderEl.querySelector('.slider-normal-next');
    const prevEl = sliderEl.querySelector('.slider-normal-prev');

    new Swiper(sliderEl, {
      modules: [Navigation],
      slidesPerView: 3,
      spaceBetween: 30,
      loop: true,
      speed: 1000,
      navigation: {
        nextEl: nextEl,
        prevEl: prevEl,
      },
      breakpoints: {
        1000: {
          slidesPerView: 3,
        },
        768: {
          slidesPerView: 2.2,
          spaceBetween: 20,
        },
        0: {
          slidesPerView: 1.2,
          spaceBetween: 20,
        },
      },
    });
  });

  const videoSectionSlider = document.querySelectorAll('.video-section-slider');

  videoSectionSlider.forEach((sliderEl) => {
    const parent = sliderEl.parentElement;
    const nextEl = parent.querySelector('.video-section-next');
    const prevEl = parent.querySelector('.video-section-prev');
    const paginationEl = parent.parentElement.querySelector(
      '.video-section-pagination'
    );

    new Swiper(sliderEl, {
      modules: [Navigation, Pagination],
      slidesPerView: 1,
      spaceBetween: 30,
      loop: false,
      autoHeight: true,
      speed: 1000,
      navigation: {
        nextEl: nextEl,
        prevEl: prevEl,
      },
      pagination: {
        el: paginationEl,
        clickable: true,
      },
      breakpoints: {
        1000: {
          slidesPerView: 1,
        },
        768: {
          slidesPerView: 1,
          spaceBetween: 20,
        },
        0: {
          slidesPerView: 1,
          spaceBetween: 20,
        },
      },
    });
  });

  const swiperContentRight = new Swiper('.swiper-content-right', {
    modules: [Navigation],
    slidesPerView: 3.5,
    spaceBetween: 44,
    speed: 1000,
    breakpoints: {
      1000: {
        slidesPerView: 3.1,
        spaceBetween: 20,
      },
      680: {
        slidesPerView: 2.1,
        spaceBetween: 20,
      },
      0: {
        slidesPerView: 1.1,
        spaceBetween: 20,
      },
    },
  });

  // //mouse move effect
  // const bannerMain = document.querySelectorAll('.lava-background');
  // const bgGoos = document.querySelectorAll('.bg-gooey');

  // bannerMain.forEach((el) => {
  //   el.addEventListener('mousemove', (e) => {
  //     const { clientX, clientY } = e;

  //     bgGoos.forEach((goos) => {
  //       const { width, height } = goos.getBoundingClientRect();
  //       const x = clientX - width / 2;
  //       const y = clientY - height / 2;
  //       goos.style.inset = 'initial';
  //       goos.style.transform = `translate(${x}px, ${y}px) rotate(var(--rotate))`;
  //     });
  //   });
  // });

  // acciones de scroll con gsap
  const scrollClass1 = document.querySelectorAll('.scroll-class');
  scrollClass1.forEach((el) => {
    ScrollTrigger.create({
      trigger: el,
      start: 'top 60%',
      toggleActions: 'play reverse play reverse',
      markers: false,
      onEnter: () => {
        el.classList.add('active-enter');
      },
      onLeaveBack: () => {
        el.classList.remove('active-enter');
      },
    });
  });

  const scroll1 = document.querySelectorAll('.scroll1');
  const parentScroll1 = document.querySelector('.scroll-class');
  gsap
    .timeline({
      scrollTrigger: {
        trigger: parentScroll1,
        start: 'top 80%',
        end: 'bottom 10%',
        toggleActions: 'play none none reverse',
        markers: false,
      },
    })
    .from(scroll1, {
      y: 200,
      opacity: 0,
      duration: 1,
      ease: 'power2.inOut',
      stagger: 0.3,
    });

  const generalScroll = document.querySelectorAll('.general-scroll');
  generalScroll.forEach((el) => {
    gsap
      .timeline({
        scrollTrigger: {
          trigger: el,
          start: 'top 80%',
          end: 'bottom 10%',
          toggleActions: 'play none none reverse',
          markers: false,
        },
      })
      .from(el, {
        y: 200,
        opacity: 0,
        duration: 1,
        ease: 'power2.inOut',
      });
  });

  const titleAnimation = document.querySelectorAll('.title-animation');
  titleAnimation.forEach((el) => {
    ScrollTrigger.create({
      trigger: el,
      start: 'top 85%',
      toggleActions: 'play reverse play reverse',
      markers: false,
      onEnter: () => {
        el.classList.add('active');
      },
      onLeaveBack: () => {
        el.classList.remove('active');
      },
    });
  });

  const colAction = document.querySelectorAll('.col-action');
  const parentColAction = document.querySelector('.columns-animation');
  colAction.forEach((el) => {
    ScrollTrigger.create({
      trigger: parentColAction,
      start: 'top 45%',
      toggleActions: 'play reverse play reverse',
      markers: false,
      onEnter: () => {
        el.classList.add('active');
        el.classList.remove('active-none');
      },
      onLeaveBack: () => {
        el.classList.remove('active');
        el.classList.add('active-none');
      },
    });
  });

  // slider loader play
  const sliders = document.querySelectorAll('.main-slider');

  sliders.forEach((sliderEl, i) => {
    const parent = sliderEl.closest('.relative');
    const paginationEl = parent.querySelector('.swiper-pagination');

    let progress = 0;
    let duration = 6000;
    let isPlaying = true;
    let startTime;
    let rafId;
    let swiper;

    const playSVG = `
            <svg xmlns="http://www.w3.org/2000/svg" class="w-[1.5rem] h-[1.5rem] fill-[#146A73]" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
            </svg>`;

    const pauseSVG = `
            <svg xmlns="http://www.w3.org/2000/svg" class="w-[1.5rem] h-[1.5rem] fill-[#146A73]" viewBox="0 0 24 24">
                <path d="M6 19h4V5H6zm8-14v14h4V5h-4z"/>
            </svg>`;

    swiper = new Swiper(sliderEl, {
      modules: [Pagination, EffectFade],
      effect: 'fade',
      speed: 1000,
      autoHeight: true,
      fadeEffect: {
        crossFade: true,
      },
      loop: true,
      pagination: {
        el: paginationEl,
        clickable: true,
        renderBullet: function (index, className) {
          return `<span class="${className}" data-index="${index}">
                                <span class="number">${index + 1}</span>
                            </span>`;
        },
      },
      on: {
        init(swiperInstance) {
          swiper = swiperInstance; // asignamos el swiper real
          startCustomAutoplay();
          resetLoaders();
        },
        slideChangeTransitionStart() {
          progress = 0;
          startTime = performance.now();
          resetLoaders();
        },
      },
    });

    function startCustomAutoplay() {
      startTime = performance.now();
      loop();
    }

    function loop(now) {
      if (!isPlaying) return;
      if (!startTime) startTime = now;
      const elapsed = now - startTime;
      progress = Math.min((elapsed / duration) * 100, 100);
      if (swiper) updateLoader(swiper.realIndex, Math.round(progress));
      if (progress >= 100) {
        swiper.slideNext();
        progress = 0;
        startTime = performance.now();
      }
      rafId = requestAnimationFrame(loop);
    }

    function pauseAutoplay() {
      isPlaying = false;
      cancelAnimationFrame(rafId);
    }

    function resumeAutoplay() {
      isPlaying = true;
      startTime = performance.now() - (progress / 100) * duration;
      loop();
    }

    function resetLoaders() {
      paginationEl
        .querySelectorAll('.swiper-pagination-bullet')
        .forEach((bullet, i) => {
          const isActive = bullet.classList.contains(
            'swiper-pagination-bullet-active'
          );
          if (isActive) {
            bullet.innerHTML = `
                        <div class="bullet-content flex justify-center items-center">
                            <button class="icon playpause-btn absolute z-[1] cursor-pointer">
                                ${isPlaying ? pauseSVG : playSVG}
                            </button>    
                            <div class="percentage" style="--p: ${progress}%"></div>
                        </div>
                    `;

            setTimeout(() => {
              const percentage = bullet.querySelector('.percentage');
              if (percentage) {
                percentage.classList.add('show');
              }
            }, 100);

            const btn = bullet.querySelector('.playpause-btn');

            btn.addEventListener('click', () => {
              if (isPlaying) {
                pauseAutoplay();
                btn.innerHTML = playSVG;
              } else {
                resumeAutoplay();
                btn.innerHTML = pauseSVG;
              }
            });
          } else {
            const index = bullet.dataset.index;
            bullet.innerHTML = `<span class="number">${
              parseInt(index) + 1
            }</span>`;
          }
        });
    }

    function updateLoader(index, percent) {
      const bullet = paginationEl.querySelectorAll('.swiper-pagination-bullet')[
        index
      ];
      if (bullet) {
        const percentage = bullet.querySelector('.percentage');
        if (percentage) {
          percentage.style.setProperty('--p', `${percent}%`);
        }
      }
    }
  });

  //acordeon
  const acordeonItems = document.querySelectorAll('.acordeon-item');
  acordeonItems.forEach((item) => {
    const header = item.querySelector('.acordeon-item-header');
    header.addEventListener('click', () => {
      item.classList.toggle('active');
      acordeonItems.forEach((otherItem) => {
        if (otherItem !== item) {
          otherItem.classList.remove('active');
        }
      });
    });
  });

  //move background radial
  const parentRadials = document.querySelectorAll('.parent-radial:not(.banner-glass .parent-radial):not(.parent-radial-static)');

  parentRadials.forEach((parentRadial) => {
    const bgBlue = parentRadial.querySelector('.radial-blue');

    document.addEventListener('mousemove', (e) => {
      const rect = parentRadial.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const percentage = Math.min(
        Math.max((mouseX / rect.width) * 100, 0),
        100
      );
      /* console.log(percentage); */
      bgBlue.style.setProperty('--p', `${percentage}%`);
    });
  });

  const shareLinks = document.querySelectorAll('.social-share-popup');
  if (shareLinks) {
    shareLinks.forEach((link) => {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        const url = this.getAttribute('href');
        const popupWidth = 960;
        const popupHeight = 450;
        const left = window.screen.width / 2 - popupWidth / 2;
        const top = window.screen.height / 2 - popupHeight / 2;
        window.open(
          url,
          'social-share',
          `width=${popupWidth},height=${popupHeight},top=${top},left=${left},scrollbars=yes,resizable=yes`
        );
      });
    });
  }

  let copyTimeout;
  document.addEventListener('click', function (e) {
    if (e.target.closest('.copyLink')) {
      e.preventDefault();
      if (copyTimeout) return;
      const copyBtn = e.target.closest('.copyLink');
      const link = copyBtn.getAttribute('href');
      navigator.clipboard.writeText(link);
      copyBtn.classList.add('onCopy');
      copyTimeout = setTimeout(() => {
        copyBtn.classList.remove('onCopy');
        copyTimeout = null;
      }, 1500);
    }
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initLenis();
});

document.addEventListener('click', (event) => {
  const a = event.target.closest('a[href]');
  if (!a) return;
  const t = a.dataset.transition;
  if (t) {
    document.documentElement.setAttribute('data-transition', t);
  }
});

// Traking click
let lastClickTarget = null;
document.addEventListener(
  'click',
  (e) => {
    lastClickTarget = e.target;
    setTimeout(() => {
      lastClickTarget = null;
    }, 100);
  },
  { capture: true }
);

if (navigation.addEventListener) {
  navigation.addEventListener('navigate', (event) => {
    if (!lastClickTarget) return;

    const link = lastClickTarget.closest('a');
    if (!link) return;

    const isMenu = link.closest('.parent-nav');
    const isNavTransition = link.closest('.navtransition');

    if (!isMenu && !isNavTransition) return;

    if (!event.destination.url.includes(document.location.origin)) {
      return;
    }
    event.intercept({
      handler: async () => {
        const response = await fetch(event.destination.url);
        const text = await response.text();

        const transition = document.startViewTransition(() => {
          const body = text.match(/<body[^>]*>([\s\S]*)<\/body>/i)[1];
          document.body.innerHTML = body;

          const title = text.match(/<title[^>]*>(.*?)<\/title>/i)[1];
          document.title = title;
        });

        transition.ready.then(() => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              window.scrollTo(0, 0);
              document.documentElement.scrollTop = 0;
              document.body.scrollTop = 0;
            }, 0);
            ScrollTrigger.refresh();
            initLenis();
          });
        });
      },
      scroll: 'manual',
    });
  });

  const tabButtons = document.querySelectorAll('.tab-button');
  const tabPanels = document.querySelectorAll('.tab-content-panel');

  tabButtons.forEach((button, index) => {
    button.addEventListener('click', () => {
      tabButtons.forEach((btn) => btn.classList.remove('active'));
      button.classList.add('active');
      tabPanels.forEach((panel) => {
        panel.classList.add('hidden');
        panel.classList.remove('active');
      });
      const targetPanel = document.querySelector(`[data-tab="${index}"]`);
      if (targetPanel) {
        targetPanel.classList.remove('hidden');
        targetPanel.classList.add('active');
      }
    });
  });
}
